import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Home, Plus, HeartHandshake, Check, Trash2, Pencil, Lock, Globe,
  Sparkles, X, Loader2, Bell, TrendingUp,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import ThemeToggle from './ThemeToggle';
import ProfileSheet from './ProfileSheet';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuthGate } from '../hooks/useAuthGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/prayers`;

const CATEGORY_LABELS = {
  general: { en: 'General', es: 'General' },
  healing: { en: 'Healing', es: 'Sanidad' },
  family: { en: 'Family', es: 'Familia' },
  guidance: { en: 'Guidance', es: 'Guía' },
  thanksgiving: { en: 'Thanksgiving', es: 'Gratitud' },
  salvation: { en: 'Salvation', es: 'Salvación' },
  provision: { en: 'Provision', es: 'Provisión' },
  other: { en: 'Other', es: 'Otro' },
};

const catColor = {
  general: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  healing: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  family: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  guidance: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  thanksgiving: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
  salvation: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  provision: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  other: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
};

const PrayerPage = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { requireAuth, dialog } = useAuthGate();

  const [tab, setTab] = useState('mine');
  const [mine, setMine] = useState([]);
  const [community, setCommunity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('all');

  // Encouragement badge + notifications inbox
  const [stats, setStats] = useState(null);
  const [notif, setNotif] = useState({ events: [], unread: 0 });
  const [inboxOpen, setInboxOpen] = useState(false);

  // Create / edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', body: '', category: 'general', is_shared: false });
  const [saving, setSaving] = useState(false);
  const [profileUser, setProfileUser] = useState(null);

  const catLabel = (c) => CATEGORY_LABELS[c]?.[language === 'es' ? 'es' : 'en'] || c;

  const loadMine = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/mine`, { withCredentials: true });
      setMine(r.data?.prayers || []);
    } catch (e) { /* silent for logged-out */ }
  }, []);

  const loadCommunity = useCallback(async () => {
    try {
      const params = filterCat !== 'all' ? { category: filterCat } : {};
      const r = await axios.get(`${API}/community`, { params, withCredentials: true });
      setCommunity(r.data?.prayers || []);
    } catch (e) { /* silent */ }
  }, [filterCat]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadMine(), loadCommunity()]).finally(() => setLoading(false));
  }, [loadMine, loadCommunity]);

  // Load encouragement stats + prayer-support notifications on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, n] = await Promise.all([
          axios.get(`${API}/stats`, { withCredentials: true }),
          axios.get(`${API}/notifications`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setStats(s.data);
        setNotif(n.data);
        // Surface a milestone / support toast on arrival if there is unread news.
        if (n.data.unread > 0) {
          const unreadEvents = n.data.events.slice(0, n.data.unread);
          const ms = unreadEvents.find((e) => e.milestone);
          if (ms) {
            toast.success(t('prayer.toastMilestone', { count: ms.milestone, title: ms.prayer_title }));
          } else {
            toast(t(n.data.unread === 1 ? 'prayer.toastNewOne' : 'prayer.toastNewOther', { count: n.data.unread }));
          }
        }
      } catch (e) { /* silent for logged-out */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openInbox = async () => {
    setInboxOpen(true);
    try {
      await axios.post(`${API}/notifications/seen`, {}, { withCredentials: true });
      setNotif((prev) => ({ ...prev, unread: 0 }));
    } catch (e) { /* silent */ }
  };

  const openCreate = () => {
    if (!requireAuth(t('prayer.authAdd'))) return;
    setEditing(null);
    setForm({ title: '', body: '', category: 'general', is_shared: false });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ title: p.title, body: p.body || '', category: p.category, is_shared: p.is_shared });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error(t('prayer.needTitle'));
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/${editing.id}`, form, { withCredentials: true });
        toast.success(t('prayer.updated'));
      } else {
        await axios.post(API, form, { withCredentials: true });
        toast.success(t('prayer.added'));
      }
      setDialogOpen(false);
      await Promise.all([loadMine(), loadCommunity()]);
    } catch (e) {
      toast.error(t('prayer.wentWrong'));
    } finally {
      setSaving(false);
    }
  };

  const markAnswered = async (p) => {
    try {
      await axios.post(`${API}/${p.id}/answered`, { is_answered: !p.is_answered }, { withCredentials: true });
      toast.success(p.is_answered ? t('prayer.markedUnanswered') : t('prayer.praiseAnswered'));
      await Promise.all([loadMine(), loadCommunity()]);
    } catch (e) { toast.error(t('prayer.failed')); }
  };

  const remove = async (p) => {
    try {
      await axios.delete(`${API}/${p.id}`, { withCredentials: true });
      toast.success(t('prayer.deleted'));
      await Promise.all([loadMine(), loadCommunity()]);
    } catch (e) { toast.error(t('prayer.failed')); }
  };

  const togglePray = async (p) => {
    if (!requireAuth(t('prayer.authPray'))) return;
    // optimistic
    setCommunity((prev) => prev.map((x) => x.id === p.id
      ? { ...x, prayed_by_me: !x.prayed_by_me, pray_count: x.pray_count + (x.prayed_by_me ? -1 : 1) }
      : x));
    try {
      await axios.post(`${API}/${p.id}/pray`, {}, { withCredentials: true });
    } catch (e) {
      toast.error(t('prayer.failed'));
      loadCommunity();
    }
  };

  const CatBadge = ({ c }) => (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${catColor[c] || catColor.general}`}>
      {catLabel(c)}
    </span>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-gradient-to-r from-purple-700 to-indigo-700 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HeartHandshake className="w-6 h-6" />
            <h1 className="text-lg font-bold">{t('prayer.headerTitle')}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openInbox}
              className="relative p-2 rounded-full hover:bg-white/20"
              title={t('prayer.prayerSupport')}
              data-testid="prayer-inbox-btn"
            >
              <Bell className="w-5 h-5" />
              {notif.unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-400 text-[10px] font-bold text-slate-900 flex items-center justify-center" data-testid="prayer-inbox-unread">
                  {notif.unread > 9 ? '9+' : notif.unread}
                </span>
              )}
            </button>
            <ThemeToggle />
            <Button variant="secondary" size="sm" onClick={() => navigate('/dashboard')} data-testid="prayer-home-btn">
              <Home className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('prayer.studyCenter')}</span>
            </Button>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Weekly encouragement badge */}
        {stats && stats.week_support_count > 0 && (
          <div
            className="mb-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-4 flex items-center gap-3"
            data-testid="prayer-encouragement-badge"
          >
            <div className="w-10 h-10 rounded-full bg-amber-400/30 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-amber-600 dark:text-amber-300" />
            </div>
            <div>
              <p className="font-semibold text-sm">
                {t(stats.week_supporter_count === 1 ? 'prayer.weekSupportOne' : 'prayer.weekSupportOther', { count: stats.week_supporter_count })}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('prayer.totalSupport', { total: stats.total_support_count })}
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">
            {t('prayer.intro')}
          </p>
          <Button onClick={openCreate} data-testid="prayer-new-btn" className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-1" />
            {t('prayer.newShort')}
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="mine" data-testid="tab-mine">{t('prayer.tabMine')}</TabsTrigger>
            <TabsTrigger value="community" data-testid="tab-community">{t('prayer.tabCommunity')}</TabsTrigger>
          </TabsList>

          {/* MY PRAYERS */}
          <TabsContent value="mine">
            {loading ? (
              <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-500" /></div>
            ) : mine.length === 0 ? (
              <EmptyState
                icon={<HeartHandshake className="w-10 h-10 text-purple-400" />}
                title={t('prayer.emptyMineTitle')}
                subtitle={t('prayer.emptyMineSub')}
                action={<Button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700"><Plus className="w-4 h-4 mr-1" />{t('prayer.newShort')}</Button>}
              />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {mine.map((p) => (
                  <div key={p.id} data-testid={`my-prayer-${p.id}`}
                    className={`rounded-xl border p-4 bg-card shadow-sm ${p.is_answered ? 'border-emerald-300 dark:border-emerald-800' : 'border-border'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CatBadge c={p.category} />
                        {p.is_shared
                          ? <span className="inline-flex items-center gap-1 text-[11px] text-blue-600 dark:text-blue-400"><Globe className="w-3 h-3" />{t('prayer.shared')}</span>
                          : <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Lock className="w-3 h-3" />{t('prayer.private')}</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(p)} className="p-1.5 rounded hover:bg-accent" title={t('prayer.edit')} data-testid={`edit-prayer-${p.id}`}><Pencil className="w-4 h-4 text-muted-foreground" /></button>
                        <button onClick={() => remove(p)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30" title={t('prayer.delete')} data-testid={`delete-prayer-${p.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                      </div>
                    </div>
                    <h3 className="font-semibold mt-2">{p.title}</h3>
                    {p.body && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{p.body}</p>}
                    {p.is_answered && p.answered_note && (
                      <div className="mt-2 text-xs bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded p-2 text-emerald-700 dark:text-emerald-300">
                        <Sparkles className="w-3 h-3 inline mr-1" />{p.answered_note}
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between">
                      {p.is_shared && <span className="text-xs text-muted-foreground">{p.pray_count} {t('prayer.prayedLabel')}</span>}
                      <Button size="sm" variant={p.is_answered ? 'outline' : 'default'}
                        className={p.is_answered ? '' : 'bg-emerald-600 hover:bg-emerald-700'}
                        onClick={() => markAnswered(p)} data-testid={`answered-prayer-${p.id}`}>
                        <Check className="w-4 h-4 mr-1" />
                        {p.is_answered ? t('prayer.answered') : t('prayer.markAnswered')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* COMMUNITY WALL */}
          <TabsContent value="community">
            <div className="mb-3 flex items-center gap-2">
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger className="w-44" data-testid="community-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('prayer.allCategories')}</SelectItem>
                  {Object.keys(CATEGORY_LABELS).map((c) => (
                    <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {loading ? (
              <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-500" /></div>
            ) : community.length === 0 ? (
              <EmptyState
                icon={<Globe className="w-10 h-10 text-purple-400" />}
                title={t('prayer.emptyCommTitle')}
                subtitle={t('prayer.emptyCommSub')}
                action={<Button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700"><Plus className="w-4 h-4 mr-1" />{t('prayer.newShort')}</Button>}
              />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {community.map((p) => (
                  <div key={p.id} data-testid={`community-prayer-${p.id}`}
                    className="rounded-xl border border-border p-4 bg-card shadow-sm flex flex-col">
                    <div className="flex items-center gap-2 flex-wrap">
                      <CatBadge c={p.category} />
                      {p.is_answered && <Badge className="bg-emerald-600 text-[10px]">{t('prayer.answered')}</Badge>}
                    </div>
                    <h3 className="font-semibold mt-2">{p.title}</h3>
                    {p.body && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{p.body}</p>}
                    {p.author_id ? (
                      <button type="button" onClick={() => setProfileUser(p.author_id)} className="text-xs text-muted-foreground mt-2 text-left hover:underline hover:text-primary transition-colors" data-testid={`prayer-author-${p.id}`}>— {p.author_name}</button>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-2">— {p.author_name}</p>
                    )}
                    <div className="mt-3 pt-3 border-t flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{p.pray_count} {t('prayer.prayingLabel')}</span>
                      <Button size="sm" variant={p.prayed_by_me ? 'default' : 'outline'}
                        className={p.prayed_by_me ? 'bg-purple-600 hover:bg-purple-700' : ''}
                        onClick={() => togglePray(p)} data-testid={`pray-btn-${p.id}`}>
                        <HeartHandshake className="w-4 h-4 mr-1" />
                        {p.prayed_by_me ? t('prayer.prayed') : t('prayer.iPrayed')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="prayer-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t('prayer.editPrayer') : t('prayer.newPrayer')}</DialogTitle>
            <DialogDescription>
              {t('prayer.dialogDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('prayer.title')}</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={t('prayer.titlePlaceholder')}
                className="mt-1" data-testid="prayer-title-input" />
            </div>
            <div>
              <label className="text-sm font-medium">{t('prayer.details')}</label>
              <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={3} className="mt-1" data-testid="prayer-body-input" />
            </div>
            <div>
              <label className="text-sm font-medium">{t('prayer.category')}</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="prayer-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(CATEGORY_LABELS).map((c) => (
                    <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium flex items-center gap-1"><Globe className="w-4 h-4" />{t('prayer.shareToCommunity')}</p>
                <p className="text-xs text-muted-foreground">{t('prayer.shareHint')}</p>
              </div>
              <Switch checked={form.is_shared} onCheckedChange={(v) => setForm({ ...form, is_shared: v })} data-testid="prayer-share-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}><X className="w-4 h-4 mr-1" />{t('prayer.cancel')}</Button>
            <Button onClick={submit} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="prayer-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              {editing ? t('prayer.save') : t('prayer.addPrayer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Prayer-support inbox */}
      <Dialog open={inboxOpen} onOpenChange={setInboxOpen}>
        <DialogContent className="sm:max-w-md" data-testid="prayer-inbox-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5" />{t('prayer.prayerSupport')}</DialogTitle>
            <DialogDescription>
              {t('prayer.inboxDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {notif.events.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('prayer.inboxEmpty')}
              </p>
            ) : notif.events.map((e) => (
              <div key={e.id} className={`rounded-lg border p-3 text-sm ${e.milestone ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800' : 'border-border'}`}>
                {e.milestone ? (
                  <p className="font-medium">🎉 {t('prayer.eventMilestone', { count: e.milestone })} “{e.prayer_title}”</p>
                ) : (
                  <p><span className="font-medium">{e.actor_name}</span> {t('prayer.eventPrayedFor')} “{e.prayer_title}”</p>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {dialog}
      <ProfileSheet
        userId={profileUser}
        open={!!profileUser}
        onClose={() => setProfileUser(null)}
        language={language}
      />
    </div>
  );
};

const EmptyState = ({ icon, title, subtitle, action }) => (
  <div className="py-16 text-center flex flex-col items-center gap-3">
    {icon}
    <div>
      <p className="font-semibold">{title}</p>
      <p className="text-sm text-muted-foreground">{subtitle}</p>
    </div>
    {action}
  </div>
);

export default PrayerPage;
